import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  UserCheck, 
  Shield, 
  Handshake, 
  Target, 
  HeartHandshake,
  Building,
  ArrowRight,
  CheckCircle,
  BadgePercent
} from "lucide-react";

export default function VorteilePage() {
  const benefits = [
    {
      icon: UserCheck,
      title: "Ein Ansprechpartner",
      description: "Sie haben immer denselben Berater, der Ihre Situation kennt und versteht."
    },
    {
      icon: Shield,
      title: "Kunde der Gesellschaft",
      description: "Sie sind direkt bei der Versicherungskammer Bayern versichert – Ihr Vertrag und Ihre Betreuung bleiben dauerhaft bei uns."
    },
    {
      icon: BadgePercent,
      title: "Sonderkonditionen & Rahmenvereinbarungen",
      description: "Profitieren Sie von exklusiven Rabatten und attraktiven Sondernachlässen durch bestehende Rahmenvereinbarungen."
    },
    {
      icon: Handshake,
      title: "Klare Zuständigkeit im Schadenfall",
      description: "Im Ernstfall wissen Sie genau, wer für Sie zuständig ist – schnelle Hilfe ohne Umwege."
    },
    {
      icon: Target,
      title: "Expertise & Spezialisierung",
      description: "Fokussiert auf Architekten und Ingenieure – wir kennen Ihre spezifischen Risiken seit über 25 Jahren."
    },
    {
      icon: Building,
      title: "Starker Partner",
      description: "Versicherungskammer Bayern – der größte öffentliche Versicherer Deutschlands."
    }
  ];

  return (
    <div data-testid="vorteile-page" className="pt-8">
      {/* Hero Section */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-[#003B70] to-[#002855]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ihre Vorteile bei einer Zusammenarbeit
            </h1>
            <p className="text-xl text-white/80">
              Erfahren Sie, warum Architekten und Ingenieure auf unsere persönliche 
              Betreuung und Branchenexpertise vertrauen.
            </p>
            <div className="grid grid-cols-2 gap-y-3 gap-x-8 mt-8 text-white/80">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-sky-300 flex-shrink-0" />
                <span className="text-sm">Spezialisiert auf Planungsrisiken</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-sky-300 flex-shrink-0" />
                <span className="text-sm">Direkte Betreuung</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-sky-300 flex-shrink-0" />
                <span className="text-sm">Bundesweit tätig</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-sky-300 flex-shrink-0" />
                <span className="text-sm">Expertise seit 25+ Jahren</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Agentur vs. Makler - Positionierung */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-[#003B70] font-semibold text-sm uppercase tracking-wider">Warum eine Agentur?</span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-3 mb-6">
                Vertrauen, Stabilität und langfristige Betreuung
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-[#003B70]/5 border border-[#003B70]/10 rounded-2xl p-8 text-center">
                <div className="w-14 h-14 rounded-xl bg-[#003B70] flex items-center justify-center mx-auto mb-4">
                  <Shield className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-3">Kunde der Gesellschaft</h3>
                <p className="text-slate-600 text-sm">
                  Sie sind immer direkt Kunde der Versicherungsgesellschaft – unabhängig von einem Vermittler.
                </p>
              </div>
              <div className="bg-[#003B70]/5 border border-[#003B70]/10 rounded-2xl p-8 text-center">
                <div className="w-14 h-14 rounded-xl bg-[#003B70] flex items-center justify-center mx-auto mb-4">
                  <UserCheck className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-3">Vertrauensvoller Partner</h3>
                <p className="text-slate-600 text-sm">
                  Bundesweit etablierte Agentur mit 25+ Jahren Expertise.
                </p>
              </div>
              <div className="bg-[#003B70]/5 border border-[#003B70]/10 rounded-2xl p-8 text-center">
                <div className="w-14 h-14 rounded-xl bg-[#003B70] flex items-center justify-center mx-auto mb-4">
                  <Handshake className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-3">Klare Zuständigkeit</h3>
                <p className="text-slate-600 text-sm">
                  Im Schadenfall wissen Sie genau, wer Ihr Ansprechpartner ist – direkt und ohne Umwege.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section data-testid="benefits-section" className="py-20 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Ihre Vorteile auf einen Blick
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Warum sich Architekten und Ingenieure für unsere Betreuung entscheiden.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                data-testid={`benefit-card-${index}`}
                className="bg-white p-8 rounded-xl border border-slate-100 shadow-sm hover:shadow-md transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-lg bg-[#003B70]/10 flex items-center justify-center mb-6">
                  <benefit.icon className="h-6 w-6 text-[#003B70]" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-slate-600">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* For Whom Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
              Für wen ist die Agenturbetreuung ideal?
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#003B70]/10 flex items-center justify-center mx-auto mb-4">
                <Building className="h-8 w-8 text-[#003B70]" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Architekturbüros</h3>
              <p className="text-slate-600 text-sm">
                Die einen verlässlichen Partner für ihre Berufshaftpflicht suchen
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#003B70]/10 flex items-center justify-center mx-auto mb-4">
                <Target className="h-8 w-8 text-[#003B70]" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Ingenieurbüros</h3>
              <p className="text-slate-600 text-sm">
                Die Wert auf Spezialisierung und Branchenkenntnis legen
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#003B70]/10 flex items-center justify-center mx-auto mb-4">
                <HeartHandshake className="h-8 w-8 text-[#003B70]" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Freiberufler</h3>
              <p className="text-slate-600 text-sm">
                Die langfristige Betreuung und einen festen Ansprechpartner schätzen
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-[#003B70] to-[#002855]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Überzeugen Sie sich selbst
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Vereinbaren Sie einen unverbindlichen Beratungstermin und erleben Sie den 
            Unterschied einer persönlichen Betreuung.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/kontakt">
              <Button 
                data-testid="vorteile-cta"
                className="bg-white text-[#003B70] hover:bg-slate-100 px-8 py-6 text-lg"
              >
                Jetzt Beratung anfragen
              </Button>
            </Link>
            <a href="tel:+493033937950">
              <Button 
                variant="outline"
                className="border-white/40 text-white hover:bg-white/10 px-8 py-6 text-lg"
              >
                +49 30 33937950
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
